import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, Circle, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import Header from '../components/Header';
import { api, WS_URL } from '../api';
import { useAuth } from '../AuthContext';

// ============================================================
// Helpers
// ============================================================
const TILE_PROVIDERS = {
  street: {
    label: 'Sokak',
    url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
    attribution: '&copy; OpenStreetMap',
  },
  dark: {
    label: 'Karanlik',
    url: 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png',
    attribution: '&copy; OpenStreetMap, &copy; CARTO',
  },
  satellite: {
    label: 'Uydu',
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    attribution: 'Tiles &copy; Esri',
  },
};

const STOP_SPEED = 2;       // km/h
const STOP_MIN_MS = 2 * 60_000;

function speedColor(speed) {
  if (speed < 5)  return '#3498db'; // mavi: dur/yavas
  if (speed < 30) return '#27ae60'; // yesil
  if (speed < 60) return '#f1c40f'; // sari
  if (speed < 90) return '#e67e22'; // turuncu
  return '#e74c3c';                  // kirmizi: hizli
}

function detectStops(points) {
  const stops = [];
  let runStart = null;
  for (let i = 0; i <= points.length; i++) {
    const slow = i < points.length && points[i].speed < STOP_SPEED;
    if (slow && runStart === null) runStart = i;
    else if (!slow && runStart !== null) {
      const startTs = new Date(points[runStart].timestamp);
      const endTs = new Date(points[i - 1].timestamp);
      const ms = endTs - startTs;
      if (ms >= STOP_MIN_MS) {
        stops.push({
          lat: points[runStart].latitude,
          lon: points[runStart].longitude,
          startTs, endTs,
          durationMin: ms / 60_000,
        });
      }
      runStart = null;
    }
  }
  return stops;
}

function formatRelative(ts, nowMs) {
  if (!ts) return '—';
  const diff = nowMs - new Date(ts).getTime();
  if (diff < 10_000) return 'simdi';
  if (diff < 60_000) return `${Math.floor(diff / 1000)} sn once`;
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)} dk once`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)} sa once`;
  return `${Math.floor(diff / 86_400_000)} gun once`;
}

function formatMinutes(min) {
  if (!min || min < 1) return '0 dk';
  const h = Math.floor(min / 60);
  const m = Math.round(min % 60);
  return h > 0 ? `${h}sa ${m}dk` : `${m} dk`;
}

function ymdLocal(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

// ============================================================
// Icons
// ============================================================
const carIcon = new L.DivIcon({
  html: `<div style="background:#e74c3c;width:18px;height:18px;border-radius:50%;border:3px solid white;box-shadow:0 0 8px rgba(0,0,0,0.4);"></div>`,
  iconSize: [24, 24],
  iconAnchor: [12, 12],
  className: 'car-icon',
});

const playbackIcon = new L.DivIcon({
  html: `<div style="background:#9b59b6;width:18px;height:18px;border-radius:50%;border:3px solid white;box-shadow:0 0 12px rgba(155,89,182,0.6);"></div>`,
  iconSize: [24, 24],
  iconAnchor: [12, 12],
  className: 'playback-icon',
});

const startIcon = new L.DivIcon({
  html: `<div style="background:#2ed573;width:14px;height:14px;border-radius:50%;border:2px solid white;"></div>`,
  iconSize: [18, 18],
  iconAnchor: [9, 9],
  className: 'start-icon',
});

const endIcon = new L.DivIcon({
  html: `<div style="background:#e94560;width:14px;height:14px;border-radius:50%;border:2px solid white;"></div>`,
  iconSize: [18, 18],
  iconAnchor: [9, 9],
  className: 'end-icon',
});

const stopIcon = new L.DivIcon({
  html: `<div style="background:#f39c12;width:14px;height:14px;border-radius:3px;border:2px solid white;"></div>`,
  iconSize: [18, 18],
  iconAnchor: [9, 9],
  className: 'stop-icon',
});

// ============================================================
// Map sub-components
// ============================================================
function MapFollower({ position, follow }) {
  const map = useMap();
  useEffect(() => {
    if (follow && position) {
      map.panTo(position, { animate: true, duration: 0.5 });
    }
  }, [map, position, follow]);
  return null;
}

function FitBoundsTrigger({ trigger, points }) {
  const map = useMap();
  const pointsRef = useRef(points);
  pointsRef.current = points;
  useEffect(() => {
    const pts = pointsRef.current;
    if (trigger > 0 && pts.length > 0) {
      const bounds = L.latLngBounds(pts.map(p => [p.latitude, p.longitude]));
      map.fitBounds(bounds, { padding: [40, 40] });
    }
  }, [trigger, map]);
  return null;
}

function MapContextMenu({ onPick }) {
  useMapEvents({
    contextmenu(e) {
      const lat = e.latlng.lat;
      const lon = e.latlng.lng;
      const text = `${lat.toFixed(6)}, ${lon.toFixed(6)}`;
      if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(text).catch(() => {});
      }
      onPick({ lat, lon });
    },
  });
  return null;
}

const GEOFENCE_ENTER_COLOR = '#27ae60';
const GEOFENCE_EXIT_COLOR  = '#e67e22';
const SPEED_ALERT_COLOR    = '#e74c3c';
function alertColor(type) {
  if (type === 'geofence_enter') return GEOFENCE_ENTER_COLOR;
  if (type === 'geofence_exit')  return GEOFENCE_EXIT_COLOR;
  if (type === 'speed_limit')    return SPEED_ALERT_COLOR;
  return '#7f8c8d';
}
function alertIcon(type) {
  if (type === 'geofence_enter') return '⮕';
  if (type === 'geofence_exit')  return '⮐';
  if (type === 'speed_limit')    return '⚡';
  return '!';
}

// ============================================================
// Main page
// ============================================================
export default function TrackerPage() {
  // Vehicles & live
  const [vehicles, setVehicles] = useState([]);
  const [selectedVehicleId, setSelectedVehicleId] = useState('vehicle-1');
  const [currentLocation, setCurrentLocation] = useState(null);
  const [trail, setTrail] = useState([]);
  const [connected, setConnected] = useState(false);

  // History
  const [days, setDays] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null); // 'YYYY-MM-DD' or null
  const [locationHistory, setLocationHistory] = useState([]);
  const [stats, setStats] = useState(null);
  const [showHistory, setShowHistory] = useState(false);

  // Playback
  const [playbackIndex, setPlaybackIndex] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(4);

  // UI
  const [followVehicle, setFollowVehicle] = useState(true);
  const [mapStyle, setMapStyle] = useState('street');
  const [fitTrigger, setFitTrigger] = useState(0);
  const [nowMs, setNowMs] = useState(Date.now());

  // Geofences & alerts
  const [geofences, setGeofences] = useState([]);
  const [showGeofences, setShowGeofences] = useState(true);
  const [alerts, setAlerts] = useState([]);
  const [showAlertList, setShowAlertList] = useState(false);
  const [toasts, setToasts] = useState([]);
  const [pickedCoord, setPickedCoord] = useState(null);

  // Demo mode
  const [demo, setDemo] = useState({ running: false, vehicle_id: null, started_at: null });
  const [demoSeedMin, setDemoSeedMin] = useState(30);
  const [demoBusy, setDemoBusy] = useState(false);
  const [demoError, setDemoError] = useState(null);

  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const wsRef = useRef(null);
  const trailRef = useRef([]);

  // ==========================================================
  // Data loading
  // ==========================================================
  const reloadVehicles = useCallback(() => {
    api.listVehicles().then(setVehicles).catch(console.error);
  }, []);
  useEffect(() => { reloadVehicles(); }, [reloadVehicles]);

  // Geofences & alerts initial load
  const reloadGeofences = useCallback(() => {
    api.listGeofences().then(setGeofences).catch(console.error);
  }, []);
  useEffect(() => { reloadGeofences(); }, [reloadGeofences]);

  const reloadAlerts = useCallback(() => {
    api.listAlerts(50).then(setAlerts).catch(console.error);
  }, []);
  useEffect(() => { reloadAlerts(); }, [reloadAlerts]);

  // Demo durumunu poll et
  const reloadDemo = useCallback(() => {
    api.demoStatus().then(setDemo).catch(() => {});
  }, []);
  useEffect(() => {
    reloadDemo();
    const id = setInterval(reloadDemo, 10_000);
    return () => clearInterval(id);
  }, [reloadDemo]);

  const onDemoStart = async () => {
    setDemoError(null); setDemoBusy(true);
    try {
      const seed = Math.max(0, Math.min(180, Number(demoSeedMin) || 0));
      const r = await api.demoStart(selectedVehicleId, seed);
      setDemo({ running: r.running, vehicle_id: r.vehicle_id, started_at: r.started_at });
      // Gecmis seed edildiyse "today" listesini ve gun listesini tazele
      if (seed > 0) {
        reloadDays();
        if (selectedDate) fetchHistory(selectedDate);
      }
    } catch (e) {
      setDemoError(e.message);
    } finally {
      setDemoBusy(false);
    }
  };

  const onDemoStop = async () => {
    setDemoError(null); setDemoBusy(true);
    try {
      await api.demoStop();
      setDemo({ running: false, vehicle_id: null, started_at: null });
    } catch (e) {
      setDemoError(e.message);
    } finally {
      setDemoBusy(false);
    }
  };

  // Toast helper
  const pushToast = useCallback((alert) => {
    const id = `${alert.id}-${Date.now()}`;
    setToasts(t => [...t, { id, alert }]);
    setTimeout(() => setToasts(t => t.filter(x => x.id !== id)), 5000);
  }, []);

  // Veri olan gunler
  const reloadDays = useCallback(() => {
    if (!selectedVehicleId) return;
    api.days(selectedVehicleId).then(setDays).catch(console.error);
  }, [selectedVehicleId]);
  useEffect(() => { reloadDays(); }, [reloadDays]);

  // WebSocket
  useEffect(() => {
    let active = true;
    function connect() {
      const ws = new WebSocket(WS_URL);
      wsRef.current = ws;
      ws.onopen = () => active && setConnected(true);
      ws.onclose = () => {
        if (!active) return;
        setConnected(false);
        setTimeout(connect, 3000);
      };
      ws.onmessage = (event) => {
        const msg = JSON.parse(event.data);
        if (msg.type === 'location_update' && msg.data.vehicle_id === selectedVehicleId) {
          const loc = msg.data;
          setCurrentLocation(loc);
          const point = [loc.latitude, loc.longitude];
          trailRef.current = [...trailRef.current.slice(-200), point];
          setTrail([...trailRef.current]);
        }
        if (msg.type === 'alert') {
          setAlerts(prev => [msg.data, ...prev].slice(0, 50));
          pushToast(msg.data);
        }
      };
    }
    connect();
    return () => { active = false; wsRef.current?.close(); };
  }, [selectedVehicleId, pushToast]);

  // Vehicle change reset
  useEffect(() => {
    trailRef.current = [];
    setTrail([]);
    setCurrentLocation(null);
    setLocationHistory([]);
    setStats(null);
    setSelectedDate(null);
    setPlaying(false);
    setPlaybackIndex(0);
  }, [selectedVehicleId]);

  // Tick clock for relative time
  useEffect(() => {
    const id = setInterval(() => setNowMs(Date.now()), 5000);
    return () => clearInterval(id);
  }, []);

  // History fetch
  const fetchHistory = useCallback((dateStr) => {
    if (!dateStr) return;
    const start = `${dateStr}T00:00:00.000Z`;
    const end = `${dateStr}T23:59:59.999Z`;
    Promise.all([
      api.history(selectedVehicleId, start, end),
      api.stats(selectedVehicleId, start, end),
    ]).then(([h, s]) => {
      const locs = h.locations || [];
      setLocationHistory(locs);
      setStats(s);
      setPlaybackIndex(0);
      setPlaying(false);
      if (locs.length > 0) setFitTrigger(t => t + 1);
    }).catch(console.error);
  }, [selectedVehicleId]);

  const onSelectDay = (dateStr) => {
    setSelectedDate(dateStr);
    setShowHistory(true);
    fetchHistory(dateStr);
  };

  const onQuickPreset = (offsetDays) => {
    const d = new Date();
    d.setDate(d.getDate() - offsetDays);
    onSelectDay(ymdLocal(d));
  };

  // Playback timer
  useEffect(() => {
    if (!playing) return;
    if (locationHistory.length < 2) { setPlaying(false); return; }
    const stepMs = Math.max(16, Math.floor(200 / playbackSpeed));
    const id = setInterval(() => {
      setPlaybackIndex(i => {
        if (i >= locationHistory.length - 1) {
          setPlaying(false);
          return i;
        }
        return i + 1;
      });
    }, stepMs);
    return () => clearInterval(id);
  }, [playing, playbackSpeed, locationHistory.length]);

  // ==========================================================
  // Derived
  // ==========================================================
  const selectedVehicle = vehicles.find(v => v.id === selectedVehicleId);

  const stops = useMemo(() => {
    if (!showHistory || locationHistory.length === 0) return [];
    return detectStops(locationHistory);
  }, [showHistory, locationHistory]);

  // Hiza gore renkli segmentler
  const speedSegments = useMemo(() => {
    if (!showHistory || locationHistory.length < 2) return [];
    const segs = [];
    let cur = null;
    for (let i = 1; i < locationHistory.length; i++) {
      const a = locationHistory[i - 1], b = locationHistory[i];
      const c = speedColor(b.speed);
      if (!cur || cur.color !== c) {
        if (cur) segs.push(cur);
        cur = { color: c, positions: [[a.latitude, a.longitude]] };
      }
      cur.positions.push([b.latitude, b.longitude]);
    }
    if (cur) segs.push(cur);
    return segs;
  }, [showHistory, locationHistory]);

  const playbackPoint = playing || playbackIndex > 0
    ? locationHistory[Math.min(playbackIndex, locationHistory.length - 1)]
    : null;

  const mapCenter = currentLocation
    ? [currentLocation.latitude, currentLocation.longitude]
    : (selectedVehicle?.latitude
        ? [selectedVehicle.latitude, selectedVehicle.longitude]
        : [37.7765, 29.0864]);

  const followPosition = playbackPoint
    ? [playbackPoint.latitude, playbackPoint.longitude]
    : (currentLocation ? [currentLocation.latitude, currentLocation.longitude] : null);

  // ==========================================================
  // Render
  // ==========================================================
  return (
    <div className="app">
      <Header rightExtra={
        <span className={`status ${connected ? 'online' : 'offline'}`}>
          {connected ? 'Bagli' : 'Baglanti Kesik'}
        </span>
      } />

      <div className="main">
        <aside className="sidebar">
          {/* CANLI */}
          <div className="panel">
            <h3>Canli Konum</h3>
            {currentLocation ? (
              <div className="info-grid">
                <div className="info-item">
                  <span className="label">Enlem</span>
                  <span className="value">{currentLocation.latitude.toFixed(6)}</span>
                </div>
                <div className="info-item">
                  <span className="label">Boylam</span>
                  <span className="value">{currentLocation.longitude.toFixed(6)}</span>
                </div>
                <div className="info-item">
                  <span className="label">Hiz</span>
                  <span className="value speed">{Math.round(currentLocation.speed)} km/h</span>
                </div>
                <div className="info-item">
                  <span className="label">Yon</span>
                  <span className="value">{Number(currentLocation.heading).toFixed(0)}°</span>
                </div>
                <div className="info-item full-width">
                  <span className="label">Son Guncelleme</span>
                  <span className="value">{formatRelative(currentLocation.timestamp, nowMs)}</span>
                </div>
              </div>
            ) : (
              <p className="no-data">
                {selectedVehicle?.last_seen
                  ? `Son goruldu: ${formatRelative(selectedVehicle.last_seen, nowMs)}`
                  : 'Konum verisi bekleniyor...'}
              </p>
            )}
          </div>

          {/* KONTROL */}
          <div className="panel">
            <h3>Harita</h3>
            <label className="toggle">
              <input
                type="checkbox"
                checked={followVehicle}
                onChange={e => setFollowVehicle(e.target.checked)}
              />
              <span>Araci Takip Et</span>
            </label>
            <div className="map-style-row">
              {Object.entries(TILE_PROVIDERS).map(([k, v]) => (
                <button
                  key={k}
                  className={`map-style-btn ${mapStyle === k ? 'active' : ''}`}
                  onClick={() => setMapStyle(k)}
                >{v.label}</button>
              ))}
            </div>
          </div>

          {/* DEMO MODU (admin) */}
          {isAdmin && (
            <div className="panel demo-panel">
              <div className="alert-panel-head">
                <h3>Demo Modu</h3>
                <span className={`demo-badge ${demo.running ? 'on' : 'off'}`}>
                  {demo.running ? 'CANLI' : 'KAPALI'}
                </span>
              </div>
              <p className="demo-desc">
                Sahte arac hareketi uretir (Denizli rotasi). Sunum/test icin —
                ESP32 verisi geldigi gibi gorunur, gecmis &amp; playback calisir.
              </p>
              {demoError && <div className="admin-error" style={{ marginTop: 6 }}>{demoError}</div>}
              {demo.running ? (
                <>
                  <div className="demo-info">
                    <span>Arac:</span> <code>{demo.vehicle_id}</code>
                    {demo.started_at && (
                      <>
                        <br /><span>Basladi:</span>{' '}
                        <code>{new Date(demo.started_at).toLocaleTimeString('tr-TR')}</code>
                      </>
                    )}
                  </div>
                  <button
                    className="btn-danger demo-btn"
                    onClick={onDemoStop}
                    disabled={demoBusy}
                  >
                    {demoBusy ? '...' : '⏹ Demo Durdur'}
                  </button>
                </>
              ) : (
                <>
                  <label className="demo-seed">
                    <span>Gecmis seed (dk):</span>
                    <input
                      type="number" min={0} max={180}
                      value={demoSeedMin}
                      onChange={e => setDemoSeedMin(e.target.value)}
                    />
                  </label>
                  <button
                    className="btn-primary demo-btn"
                    onClick={onDemoStart}
                    disabled={demoBusy || !selectedVehicleId}
                    title={`Secili arac: ${selectedVehicleId}`}
                  >
                    {demoBusy ? '...' : '▶ Demo Baslat'}
                  </button>
                </>
              )}
            </div>
          )}

          {/* GECMIS */}
          <div className="panel">
            <h3>Konum Gecmisi</h3>
            <label className="toggle">
              <input
                type="checkbox"
                checked={showHistory}
                onChange={e => setShowHistory(e.target.checked)}
              />
              <span>Gecmisi Goster</span>
            </label>

            {showHistory && (
              <>
                <div className="quick-presets">
                  <button className="btn-secondary" onClick={() => onQuickPreset(0)}>Bugun</button>
                  <button className="btn-secondary" onClick={() => onQuickPreset(1)}>Dun</button>
                  <button className="btn-secondary" onClick={reloadDays} title="Gun listesini yenile">↻</button>
                </div>

                {days.length === 0 ? (
                  <p className="no-data">Henuz veri yok.</p>
                ) : (
                  <div className="day-list">
                    {days.map(d => {
                      const dt = new Date(d.date + 'T00:00:00');
                      const label = dt.toLocaleDateString('tr-TR', {
                        day: '2-digit', month: 'short', weekday: 'short'
                      });
                      return (
                        <button
                          key={d.date}
                          className={`day-row ${selectedDate === d.date ? 'active' : ''}`}
                          onClick={() => onSelectDay(d.date)}
                        >
                          <span className="day-label">{label}</span>
                          <span className="day-meta">
                            <span className="day-count">{d.count}</span>
                            <span className="day-max">{Math.round(d.max_speed)} km/h</span>
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}

                {stats && stats.total_points > 0 && (
                  <div className="stats stats-grid">
                    <div><span>Mesafe</span><strong>{stats.total_distance_km.toFixed(1)} km</strong></div>
                    <div><span>Nokta</span><strong>{stats.total_points}</strong></div>
                    <div><span>Hareket</span><strong>{formatMinutes(stats.moving_minutes)}</strong></div>
                    <div><span>Bekleme</span><strong>{formatMinutes(stats.idle_minutes)}</strong></div>
                    <div><span>Ort.</span><strong>{stats.avg_speed.toFixed(1)} km/h</strong></div>
                    <div><span>Maks.</span><strong>{stats.max_speed.toFixed(1)} km/h</strong></div>
                    <div><span>Durak</span><strong>{stats.stop_count}</strong></div>
                  </div>
                )}

                {locationHistory.length > 1 && (
                  <button
                    className="btn-secondary fit-btn"
                    onClick={() => setFitTrigger(t => t + 1)}
                  >Haritaya Sigdir</button>
                )}
              </>
            )}
          </div>

          {/* PLAYBACK */}
          {showHistory && locationHistory.length > 1 && (
            <div className="panel">
              <h3>Oynatici</h3>
              <div className="playback-row">
                <button
                  className="btn-primary playback-play"
                  onClick={() => {
                    if (playbackIndex >= locationHistory.length - 1) setPlaybackIndex(0);
                    setPlaying(p => !p);
                  }}
                >{playing ? '⏸ Durdur' : '▶ Oynat'}</button>
                <div className="playback-speeds">
                  {[1, 4, 16].map(s => (
                    <button
                      key={s}
                      className={`speed-btn ${playbackSpeed === s ? 'active' : ''}`}
                      onClick={() => setPlaybackSpeed(s)}
                    >{s}x</button>
                  ))}
                </div>
              </div>
              <input
                type="range"
                min={0}
                max={Math.max(0, locationHistory.length - 1)}
                value={playbackIndex}
                onChange={e => setPlaybackIndex(Number(e.target.value))}
                className="playback-seek"
              />
              {playbackPoint && (
                <div className="playback-info">
                  <span>{new Date(playbackPoint.timestamp).toLocaleTimeString('tr-TR')}</span>
                  <span className="speed-pill" style={{ background: speedColor(playbackPoint.speed) }}>
                    {Math.round(playbackPoint.speed)} km/h
                  </span>
                </div>
              )}
            </div>
          )}

          {/* GEOFENCE & ALARM */}
          <div className="panel">
            <div className="alert-panel-head">
              <h3>Alarm & Geofence</h3>
              <span className={`alert-badge ${alerts.length > 0 ? 'has' : ''}`}>{alerts.length}</span>
            </div>
            <label className="toggle">
              <input
                type="checkbox"
                checked={showGeofences}
                onChange={e => setShowGeofences(e.target.checked)}
              />
              <span>Geofence'leri Goster ({geofences.filter(g => g.active).length} aktif)</span>
            </label>
            {pickedCoord && (
              <div className="picked-coord">
                Secilen: <code>{pickedCoord.lat.toFixed(6)}, {pickedCoord.lon.toFixed(6)}</code>
                {isAdmin && <span> — admin panelinden geofence olustur</span>}
              </div>
            )}
            <button
              className="btn-secondary alert-toggle-btn"
              onClick={() => setShowAlertList(s => !s)}
            >{showAlertList ? 'Listeyi Gizle' : `Son Alarmlari Goster (${alerts.length})`}</button>
            {showAlertList && (
              <div className="alert-list">
                {alerts.length === 0 && <p className="no-data">Alarm yok.</p>}
                {alerts.slice(0, 20).map(a => (
                  <div key={a.id} className="alert-item" style={{ borderLeftColor: alertColor(a.type) }}>
                    <div className="alert-item-head">
                      <span className="alert-icon" style={{ color: alertColor(a.type) }}>{alertIcon(a.type)}</span>
                      <span className="alert-time">{new Date(a.ts).toLocaleString('tr-TR')}</span>
                    </div>
                    <div className="alert-msg">{a.message}</div>
                  </div>
                ))}
                {isAdmin && alerts.length > 0 && (
                  <button
                    className="btn-danger alert-clear-btn"
                    onClick={async () => {
                      if (!window.confirm('Tum alarmlar silinsin mi?')) return;
                      try { await api.clearAlerts(); setAlerts([]); } catch (e) { console.error(e); }
                    }}
                  >Tumunu Temizle</button>
                )}
              </div>
            )}
          </div>

          {/* ARAÇLAR */}
          <div className="panel">
            <h3>Araclar</h3>
            {vehicles.length === 0 && <p className="no-data">Kayitli arac yok.</p>}
            {vehicles.map(v => (
              <div
                key={v.id}
                className={`vehicle-card ${v.id === selectedVehicleId ? 'selected' : ''}`}
                onClick={() => setSelectedVehicleId(v.id)}
                style={{ cursor: 'pointer' }}
              >
                <div className="vehicle-info">
                  <div className="vehicle-name">{v.name}</div>
                  {v.last_seen && (
                    <div className="vehicle-last-seen">{formatRelative(v.last_seen, nowMs)}</div>
                  )}
                </div>
                <div className="vehicle-plate">{v.plate}</div>
              </div>
            ))}
          </div>
        </aside>

        <div className="map-container">
          <MapContainer center={mapCenter} zoom={14} style={{ height: '100%', width: '100%' }}>
            <TileLayer
              key={mapStyle}
              attribution={TILE_PROVIDERS[mapStyle].attribution}
              url={TILE_PROVIDERS[mapStyle].url}
            />
            <MapFollower position={followPosition} follow={followVehicle} />
            <FitBoundsTrigger trigger={fitTrigger} points={locationHistory} />
            <MapContextMenu onPick={setPickedCoord} />

            {/* Geofence daireleri */}
            {showGeofences && geofences.filter(g => g.active && (
              !g.vehicle_id || g.vehicle_id === selectedVehicleId
            )).map(g => (
              <Circle
                key={g.id}
                center={[g.center_lat, g.center_lon]}
                radius={g.radius_m}
                pathOptions={{
                  color: '#3498db',
                  weight: 2,
                  fillColor: '#3498db',
                  fillOpacity: 0.08,
                }}
              >
                <Popup>
                  <strong>{g.name}</strong><br />
                  {g.vehicle_id ? `Arac: ${g.vehicle_id}` : 'Tum araclar'}<br />
                  Yaricap: {g.radius_m >= 1000 ? `${(g.radius_m/1000).toFixed(1)} km` : `${Math.round(g.radius_m)} m`}
                </Popup>
              </Circle>
            ))}

            {/* Canli iz */}
            {!showHistory && trail.length > 1 && (
              <Polyline positions={trail} color="#e74c3c" weight={3} opacity={0.7} />
            )}

            {/* Canli marker */}
            {currentLocation && !playbackPoint && (
              <Marker position={[currentLocation.latitude, currentLocation.longitude]} icon={carIcon}>
                <Popup>
                  <strong>{selectedVehicle?.plate || selectedVehicleId}</strong><br />
                  Hiz: {Math.round(currentLocation.speed)} km/h<br />
                  {new Date(currentLocation.timestamp).toLocaleString('tr-TR')}
                </Popup>
              </Marker>
            )}

            {/* Geçmiş: hıza göre renkli segmentler */}
            {showHistory && speedSegments.map((seg, i) => (
              <Polyline
                key={i}
                positions={seg.positions}
                color={seg.color}
                weight={4}
                opacity={0.85}
              />
            ))}

            {/* Geçmiş: başlangıç & bitiş */}
            {showHistory && locationHistory.length > 0 && (
              <>
                <Marker
                  position={[locationHistory[0].latitude, locationHistory[0].longitude]}
                  icon={startIcon}
                >
                  <Popup>
                    <strong>Baslangic</strong><br />
                    {new Date(locationHistory[0].timestamp).toLocaleString('tr-TR')}
                  </Popup>
                </Marker>
                <Marker
                  position={[
                    locationHistory[locationHistory.length - 1].latitude,
                    locationHistory[locationHistory.length - 1].longitude,
                  ]}
                  icon={endIcon}
                >
                  <Popup>
                    <strong>Bitis</strong><br />
                    {new Date(locationHistory[locationHistory.length - 1].timestamp).toLocaleString('tr-TR')}
                  </Popup>
                </Marker>
              </>
            )}

            {/* Duraklar */}
            {showHistory && stops.map((s, i) => (
              <Marker key={`stop-${i}`} position={[s.lat, s.lon]} icon={stopIcon}>
                <Popup>
                  <strong>Durak ({Math.round(s.durationMin)} dk)</strong><br />
                  {s.startTs.toLocaleTimeString('tr-TR')} – {s.endTs.toLocaleTimeString('tr-TR')}
                </Popup>
              </Marker>
            ))}

            {/* Playback marker */}
            {playbackPoint && (
              <Marker
                position={[playbackPoint.latitude, playbackPoint.longitude]}
                icon={playbackIcon}
              >
                <Popup>
                  Oynatici<br />
                  {new Date(playbackPoint.timestamp).toLocaleTimeString('tr-TR')}<br />
                  {Math.round(playbackPoint.speed)} km/h
                </Popup>
              </Marker>
            )}
          </MapContainer>

          {/* Hız efsanesi */}
          {showHistory && locationHistory.length > 1 && (
            <div className="speed-legend">
              <span className="speed-legend-title">Hiz</span>
              <div className="speed-legend-bar">
                <span style={{ background: '#3498db' }}>0-5</span>
                <span style={{ background: '#27ae60' }}>5-30</span>
                <span style={{ background: '#f1c40f' }}>30-60</span>
                <span style={{ background: '#e67e22' }}>60-90</span>
                <span style={{ background: '#e74c3c' }}>90+</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {toasts.length > 0 && (
        <div className="toast-container">
          {toasts.map(t => (
            <div
              key={t.id}
              className="toast-item"
              style={{ borderLeftColor: alertColor(t.alert.type) }}
            >
              <span className="toast-icon" style={{ color: alertColor(t.alert.type) }}>
                {alertIcon(t.alert.type)}
              </span>
              <div className="toast-body">
                <div className="toast-title">
                  {t.alert.type === 'geofence_enter' && 'Geofence Girisi'}
                  {t.alert.type === 'geofence_exit' && 'Geofence Cikisi'}
                  {t.alert.type === 'speed_limit' && 'Hiz Limiti Asildi'}
                </div>
                <div className="toast-msg">{t.alert.message}</div>
              </div>
              <button
                className="toast-close"
                onClick={() => setToasts(ts => ts.filter(x => x.id !== t.id))}
                aria-label="Kapat"
              >×</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
